import { useState, useEffect } from "react";
import OrderSummaryTab from "./OrderSummaryTab";
import AddressTab from "./AddressTab";
import PaymentTab from "./PaymentTab";
import { SERVER_URL } from "../../../constants";
import { ClipLoader } from "react-spinners";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useUser } from "@clerk/clerk-react";
import axios from "axios";

const CheckoutPage = () => {
  const [activeTab, setActiveTab] = useState("summary");
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [checkoutProducts, setCheckoutProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useUser();
  const navigate = useNavigate();
  const [total, setTotal] = useState(0);
  const [pw, setPw] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [discount, setDiscount] = useState(0);
  const [finalTotal, setFinalTotal] = useState(0);

  // Load Razorpay script
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  // Fetch checkout products from API
  const fetchCheckoutProducts = async () => {
    try {
      const response = await fetch(`${SERVER_URL}/api/v1/cart/${user.id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      const result = await response.json();
      const calculatedTotal = result.cart.reduce((sum, r) => sum + r.totalAmount, 0);
      setTotal(calculatedTotal);
      setFinalTotal(calculatedTotal); // Initialize final total
      if (result.success) {
        setCheckoutProducts(result.cart || []);
      } else {
        toast.error(result.message || "Failed to load checkout products.");
      }
    } catch (error) {
      console.error("Error fetching checkout products:", error);
      toast.error("An error occurred while fetching checkout products.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCheckoutProducts();
  }, []);

  useEffect(() => {
    if (checkoutProducts.length === 0 && !loading) {
      toast.warning("No products found in checkout! Redirecting...");
      navigate("/cart");
    }
  }, [checkoutProducts, loading, navigate]);

  // Fetch user addresses
  const fetchAddresses = async () => {
    try {
      const response = await fetch(`${SERVER_URL}/api/v1/address/${user.id}`);
      const result = await response.json();
      if (result.success) {
        const fetchedAddresses = result.addresses || [];
        setAddresses(fetchedAddresses);
        if (fetchedAddresses.length === 1) {
          setSelectedAddressId(fetchedAddresses[0]._id); // Auto-select if only one address
        }
      } else {
        toast.error(result.message || "Failed to fetch addresses.");
      }
    } catch (error) {
      console.error("Error fetching addresses:", error);
    }
  };

  useEffect(() => {
    fetchAddresses();
  }, []);

  // Update product quantity
  const onUpdateQuantity = (productId, newQuantity) => {
    setCheckoutProducts((prevProducts) =>
      prevProducts.map((product) =>
        product.productId === productId
          ? { ...product, quantity: newQuantity, totalAmount: product.price * newQuantity }
          : product
      )
    );
    const updatedTotal = checkoutProducts.reduce(
      (sum, product) => sum + product.price * (product.productId === productId ? newQuantity : product.quantity),
      0
    );
    setTotal(updatedTotal);
    applyDiscount(updatedTotal); // Recalculate discount with new total
  };

  function handlePaymentMethod(e) {
    setPw(e);
  }

  // Remove product from checkout
  const onRemoveItem = (productId) => {
    setCheckoutProducts((prevProducts) =>
      prevProducts.filter((product) => product.productId !== productId)
    );
    const updatedTotal = checkoutProducts.reduce(
      (sum, product) => sum + (product.productId !== productId ? product.price * product.quantity : 0),
      0
    );
    setTotal(updatedTotal);
    applyDiscount(updatedTotal); // Recalculate discount with new total
  };

  // Apply discount based on coupon code
  const applyDiscount = (currentTotal) => {
    if (couponCode.toUpperCase() === "FEATURE10") {
      const discountAmount = currentTotal * 0.1; // 10% discount
      setDiscount(discountAmount);
      setFinalTotal(currentTotal - discountAmount);
    } else {
      setDiscount(0);
      setFinalTotal(currentTotal);
    }
  };

  const handleCouponApply = () => {
    applyDiscount(total);
    if (couponCode.toUpperCase() === "FEATURE10") {
      toast.success("Coupon applied! You saved 10%!");
    } else {
      toast.error("Invalid coupon code");
    }
  };

  const handleRazorpayPayment = async (orderId) => {
    const options = {
      key: "rzp_test_wm9GmUp0H8OEP7",
      amount: finalTotal * 100,
      currency: "INR",
      name: "Your Store Name",
      description: "Purchase Description",
      order_id: orderId,
      handler: async (response) => {
        try {
          const verifyPayment = await axios.post(`${SERVER_URL}/api/v1/order/verify-payment`, {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
            products: checkoutProducts,
            address: selectedAddressId,
            total: finalTotal,
            userId: user.id,
          });

          if (verifyPayment.data.success) {
            toast.success("Payment successful!");
            navigate("/order-success");
          } else {
            toast.error("Payment verification failed");
          }
        } catch (error) {
          console.error("Payment verification error:", error);
          toast.error("Payment verification failed");
        }
      },
      prefill: {
        name: user.fullName,
        email: user.primaryEmailAddress.emailAddress,
      },
      theme: {
        color: "#4F46E5",
      },
    };

    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  const handleCryptoPayment = async () => {
    if (typeof window.ethereum === "undefined") {
      toast.error("Please install MetaMask or another Ethereum wallet");
      return;
    }

    try {
      const accounts = await window.ethereum.request({ method: "eth_requestAccounts" });
      const account = accounts[0];
      const chainId = await window.ethereum.request({ method: "eth_chainId" });
      const networkName = {
        "0x1": "Mainnet",
        "0x3": "Ropsten",
        "0x4": "Rinkeby",
        "0x5": "Goerli",
        "0xaa36a7": "Sepolia",
      }[chainId] || "Unknown Network";

      const response = await axios.get("https://api.coingecko.com/api/v3/simple/price?ids=ethereum&vs_currencies=inr");
      const ethPriceInInr = response.data.ethereum.inr;

      if (!ethPriceInInr) {
        toast.error("Failed to fetch ETH exchange rate");
        return;
      }

      const ethAmount = finalTotal / ethPriceInInr;
      const formattedEthAmount = ethAmount.toFixed(6);
      const weiValue = (ethAmount * 1e18).toString(16);
      const weiHex = `0x${weiValue}`;

      toast.info(
        `You will pay ${formattedEthAmount} ETH (₹${finalTotal.toFixed(2)}) at ₹${ethPriceInInr.toFixed(
          2
        )} per ETH on ${networkName}. Confirm in your wallet.`
      );

      const transactionParams = {
        from: account,
        to: "0x1234567890123456789012345678901234567890",
        value: weiHex,
      };

      const txHash = await window.ethereum.request({
        method: "eth_sendTransaction",
        params: [transactionParams],
      });

      toast.info("Transaction submitted, awaiting confirmation...");

      const verificationResponse = await axios.post(`${SERVER_URL}/api/v1/order/crypto`, {
        transactionHash: txHash,
        total: finalTotal,
        ethAmount: formattedEthAmount,
        products: checkoutProducts,
        address: selectedAddressId,
        userId: user.id,
        network: networkName,
      });

      if (verificationResponse.data.success) {
        toast.success("Crypto payment successful!");
        navigate("/");
      } else {
        toast.error(verificationResponse.data.message || "Crypto payment verification failed");
      }
    } catch (error) {
      console.error("Crypto payment error:", error);
      let errorMessage = "Crypto payment failed";
      if (error.code === 4001) errorMessage = "Transaction rejected by user";
      else if (error.code === -32603) errorMessage = "Transaction error. Check wallet balance.";
      else if (error.message?.includes("insufficient funds")) errorMessage = "Insufficient funds";
      toast.error(errorMessage);
    }
  };

  const processCheckout = async (finalTotal, products, address) => {
    if (!pw || pw === "") {
      toast.error("Please select a payment method");
      return;
    }
    if (!selectedAddressId) {
      if (addresses.length === 0) {
        toast.error("Please add a delivery address");
      } else {
        toast.error("Please select a delivery address");
      }
      return;
    }

    if (pw === "cod") {
      try {
        const payload = { total: finalTotal, products, address, userId: user.id };
        const res = await axios.post(`${SERVER_URL}/api/v1/order/cod`, payload);
        if (res.data.message === "s") {
          toast.success("Order created successfully");
          navigate("/");
        }
      } catch (error) {
        console.error("COD order error:", error);
        toast.error("Failed to create order");
      }
    } else if (pw === "online") {
      try {
        const response = await axios.post(`${SERVER_URL}/api/v1/order/create-razorpay-order`, {
          amount: finalTotal,
          currency: "INR",
        });
        if (response.data.success) {
          handleRazorpayPayment(response.data.orderId);
        } else {
          toast.error("Failed to create order");
        }
      } catch (error) {
        console.error("Error creating order:", error);
        toast.error("Failed to initiate payment");
      }
    } else if (pw === "crypto") {
      await handleCryptoPayment();
    }
  };

  return (
    <div className="container mx-auto px-4 py-10 mt-10 mb-40">
      <h1 className="text-3xl font-bold mb-8">Checkout</h1>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <span className="bg-indigo-600 text-white rounded-full w-6 h-6 inline-flex items-center justify-center text-sm mr-3">1</span>
              Order Summary
            </h2>
            <div className="border-b pb-6">
              <OrderSummaryTab
                checkoutProducts={checkoutProducts}
                onUpdateQuantity={onUpdateQuantity}
                onRemoveItem={onRemoveItem}
              />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <span className="bg-indigo-600 text-white rounded-full w-6 h-6 inline-flex items-center justify-center text-sm mr-3">2</span>
              Shipping Address
            </h2>
            <div className="border-b pb-6">
              <AddressTab
                addresses={addresses}
                setAddresses={setAddresses}
                selectedAddressId={selectedAddressId}
                setSelectedAddressId={setSelectedAddressId}
              />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <span className="bg-indigo-600 text-white rounded-full w-6 h-6 inline-flex items-center justify-center text-sm mr-3">3</span>
              Payment Method
            </h2>
            <PaymentTab
              total={finalTotal}
              pm={handlePaymentMethod}
              checkoutProducts={checkoutProducts}
              selectedAddressId={selectedAddressId}
            />
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-md p-6 sticky top-24">
            <h2 className="text-xl font-semibold mb-4">Order Total</h2>
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-gray-600">Subtotal</span>
                <span className="font-medium">₹{total.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Shipping</span>
                <span className="font-medium">Free</span>
              </div>
              <div className="relative mt-4">
                <input
                  type="text"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value)}
                  placeholder="Enter coupon code"
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                />
                <button
                  onClick={handleCouponApply}
                  className="absolute right-0 top-0 h-full px-4 bg-indigo-600 text-white rounded-r-lg hover:bg-indigo-700 transition-colors"
                >
                  Apply
                </button>
              </div>
              {discount > 0 && (
                <div className="flex justify-between text-green-600 animate-pulse">
                  <span className="font-semibold">Discount (10%)</span>
                  <span className="font-bold">-₹{discount.toFixed(2)}</span>
                </div>
              )}
              <div className="border-t pt-4">
                <div className="flex justify-between">
                  <span className="font-semibold">Total</span>
                  <span
                    className={`text-xl font-bold transition-all duration-500 ${
                      discount > 0 ? "text-green-600 scale-110" : "text-indigo-600"
                    }`}
                  >
                    ₹{finalTotal.toFixed(2)}
                  </span>
                </div>
              </div>
              <button
                className="w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
                onClick={() => processCheckout(finalTotal, checkoutProducts, selectedAddressId)}
              >
                Place Order
              </button>
            </div>
          </div>
        </div>
      </div>

      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center">
          <ClipLoader size={150} color="#4A90E2" />
        </div>
      )}
    </div>
  );
};

export default CheckoutPage;
